import React, { useState, useEffect } from 'react';
import { useGame } from '../../context/GameContext';
import {
  LIFE_SKILLS,
  LIFE_SKILLS_CONFIG,
  getSkillDisplayInfo,
  SKILLING_ACTIONS,
  SKILLING_CONFIG,
  getActionsForSkill,
  getNextMilestone,
} from '../../data/lifeSkills';
import './LifeSkillsPanel.css';

/**
 * Life Skills Panel - MWI-Style XP Grind System
 *
 * Left: Actions & Queue (functional)
 * Right: Skill Info & Bonuses (informational)
 */
const LifeSkillsPanel = () => {
  const {
    playerCharacter,
    currentSkillingAction,
    skillingQueue,
    startSkillingAction,
    cancelSkillingAction,
    clearSkillingQueue,
    removeFromSkillingQueue,
    getSkillingProgressInfo,
  } = useGame();

  const lifeSkills = playerCharacter.lifeSkills || {};

  // Selected skill tab
  const [selectedSkill, setSelectedSkill] = useState('lumbering');

  // Quantity selection for actions
  const [selectedQuantity, setSelectedQuantity] = useState(10);

  // Progress update timer
  const [, setTick] = useState(0);

  // Update progress display at 10 FPS
  useEffect(() => {
    if (!currentSkillingAction) return;
    const interval = setInterval(() => setTick(t => t + 1), 100);
    return () => clearInterval(interval);
  }, [currentSkillingAction]);

  // Get all gathering skills (only gathering skills have skilling actions)
  const gatheringSkillIds = Object.keys(LIFE_SKILLS).filter(
    id => LIFE_SKILLS[id].category === 'gathering'
  );

  // Get actions for selected skill
  const skillActions = getActionsForSkill(selectedSkill);
  const currentSkillLevel = lifeSkills[selectedSkill]?.level || 1;
  const currentSkillInfo = getSkillDisplayInfo(selectedSkill, lifeSkills[selectedSkill]);

  // Get progress info
  const progressInfo = getSkillingProgressInfo();

  // Format time as Xs or Xm Xs
  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  // Format percent bonus
  const formatPercent = (value) => {
    if (value === 0) return '0%';
    return `+${Math.round(value * 100)}%`;
  };

  // Calculate XP per hour for an action
  const calcXpPerHour = (action) => {
    const actionsPerHour = 3600 / action.actionTime;
    return Math.round(actionsPerHour * action.xpGain);
  };

  // Handle action click
  const handleActionClick = (actionId) => {
    const count = selectedQuantity === 'infinite' ? Infinity : selectedQuantity;
    startSkillingAction(actionId, count);
  };

  // Check if action is unlocked
  const isActionUnlocked = (action) => {
    return currentSkillLevel >= action.levelReq;
  };

  // Get all skills info for the right panel
  const getAllSkillsInfo = () => {
    return Object.entries(LIFE_SKILLS)
      .filter(([_, skill]) => skill.category === 'gathering')
      .map(([skillId, skill]) => {
        const playerSkill = lifeSkills[skillId] || { level: 1, xp: 0 };
        const info = getSkillDisplayInfo(skillId, playerSkill);
        const nextMilestone = getNextMilestone(skillId, playerSkill.level);
        return {
          id: skillId,
          ...skill,
          level: playerSkill.level,
          xp: playerSkill.xp,
          xpForNext: info?.xpForNext || 100,
          progressPercent: info?.progressPercent || 0,
          totalBonuses: info?.totalBonuses || {},
          nextMilestone,
        };
      });
  };

  const allSkillsInfo = getAllSkillsInfo();

  return (
    <div className="ls-panel">
      {/* Header */}
      <div className="ls-header">
        <h3>🌿 Life Skills</h3>
        <span className="ls-header-subtitle">Train skills to boost World Map gathering</span>
      </div>

      <div className="ls-main-content">
        {/* ========== LEFT COLUMN: Actions & Queue ========== */}
        <div className="ls-left-column">
          {/* Current Action Display */}
          <div className="ls-section">
            <div className="ls-section-header">
              <span className="ls-section-title">Current Action</span>
            </div>
            <div className="ls-section-body">
              {progressInfo ? (
                <div className="ls-current-action">
                  <div className="ls-action-row">
                    <span className="ls-action-icon">{progressInfo.action.icon}</span>
                    <span className="ls-action-name">{progressInfo.action.name}</span>
                    <span className="ls-action-repeat">
                      {progressInfo.isInfinite ? '∞' : `×${progressInfo.repeatCount}`}
                    </span>
                  </div>
                  <div className="ls-progress-bar">
                    <div
                      className="ls-progress-fill"
                      style={{ width: `${progressInfo.percent}%` }}
                    />
                  </div>
                  <div className="ls-action-footer">
                    <span className="ls-xp-gain">+{progressInfo.action.xpGain} XP</span>
                    <span className="ls-time-remaining">{formatTime(progressInfo.remaining)}</span>
                  </div>
                  <button className="ls-cancel-btn" onClick={cancelSkillingAction}>
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="ls-idle">
                  <span className="ls-idle-icon">💤</span>
                  <span className="ls-idle-text">Idle - Select an action below</span>
                </div>
              )}
            </div>
          </div>

          {/* Skill Tabs */}
          <div className="ls-section">
            <div className="ls-section-header">
              <span className="ls-section-title">Select Skill</span>
            </div>
            <div className="ls-section-body">
              <div className="ls-skill-tabs">
                {gatheringSkillIds.map(skillId => {
                  const skill = LIFE_SKILLS[skillId];
                  const level = lifeSkills[skillId]?.level || 1;
                  const isActive = selectedSkill === skillId;
                  return (
                    <button
                      key={skillId}
                      className={`ls-skill-tab ${isActive ? 'active' : ''}`}
                      onClick={() => setSelectedSkill(skillId)}
                    >
                      <span className="ls-tab-icon">{skill.icon}</span>
                      <div className="ls-tab-info">
                        <span className="ls-tab-name">{skill.name}</span>
                        <span className="ls-tab-level">Lv.{level}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Selected Skill Actions */}
          <div className="ls-section ls-section-actions">
            <div className="ls-section-header">
              <span className="ls-section-title">
                {LIFE_SKILLS[selectedSkill]?.icon} {LIFE_SKILLS[selectedSkill]?.name} Actions
              </span>
              {currentSkillInfo && (
                <span className="ls-skill-xp">
                  {currentSkillInfo.xp}/{currentSkillInfo.xpForNext} XP
                </span>
              )}
            </div>
            <div className="ls-section-body">
              {/* Skill XP Bar */}
              {currentSkillInfo && (
                <div className="ls-skill-progress">
                  <div className="ls-skill-bar">
                    <div
                      className="ls-skill-bar-fill"
                      style={{ width: `${currentSkillInfo.progressPercent}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Actions Grid */}
              <div className="ls-actions-grid">
                {skillActions.map(action => {
                  const unlocked = isActionUnlocked(action);
                  const xpPerHour = calcXpPerHour(action);
                  return (
                    <div
                      key={action.id}
                      className={`ls-action-card ${unlocked ? 'unlocked' : 'locked'}`}
                      onClick={() => unlocked && handleActionClick(action.id)}
                    >
                      <div className="ls-card-icon">{action.icon}</div>
                      <div className="ls-card-name">{action.name}</div>
                      {unlocked ? (
                        <>
                          <div className="ls-card-xp">+{action.xpGain} XP</div>
                          <div className="ls-card-meta">
                            <span className="ls-card-time">{action.actionTime}s</span>
                            <span className="ls-card-rate">{(xpPerHour / 1000).toFixed(1)}k/hr</span>
                          </div>
                        </>
                      ) : (
                        <div className="ls-card-locked">
                          🔒 Lv.{action.levelReq}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Quantity Selector */}
              <div className="ls-quantity">
                <span className="ls-quantity-label">Repeat:</span>
                <div className="ls-quantity-btns">
                  {[1, 10, 50, 'infinite'].map(qty => (
                    <button
                      key={qty}
                      className={`ls-qty-btn ${selectedQuantity === qty ? 'active' : ''}`}
                      onClick={() => setSelectedQuantity(qty)}
                    >
                      {qty === 'infinite' ? '∞' : `×${qty}`}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Queue Display */}
          <div className="ls-section ls-section-queue">
            <div className="ls-section-header">
              <span className="ls-section-title">
                Queue ({skillingQueue.length}/{SKILLING_CONFIG.maxQueueSize})
              </span>
              {skillingQueue.length > 0 && (
                <button className="ls-clear-btn" onClick={clearSkillingQueue}>
                  Clear
                </button>
              )}
            </div>
            <div className="ls-section-body">
              {skillingQueue.length > 0 ? (
                <div className="ls-queue-list">
                  {skillingQueue.map((item, index) => {
                    const action = SKILLING_ACTIONS[item.actionId];
                    return (
                      <div key={index} className="ls-queue-item">
                        <span className="ls-queue-num">{index + 1}.</span>
                        <span className="ls-queue-icon">{action?.icon}</span>
                        <span className="ls-queue-name">{action?.name}</span>
                        <span className="ls-queue-count">
                          {item.count === Infinity ? '∞' : `×${item.count}`}
                        </span>
                        <button
                          className="ls-queue-remove"
                          onClick={() => removeFromSkillingQueue(index)}
                        >
                          ✕
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="ls-queue-empty">
                  Click actions to add to queue
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ========== RIGHT COLUMN: Info & Bonuses ========== */}
        <div className="ls-right-column">
          {/* How It Works */}
          <div className="ls-section ls-info-section">
            <div className="ls-section-header">
              <span className="ls-section-title">How It Works</span>
            </div>
            <div className="ls-section-body">
              <div className="ls-info-list">
                <div className="ls-info-item">
                  <span className="ls-info-icon">🎯</span>
                  <span>Click actions to train skills</span>
                </div>
                <div className="ls-info-item">
                  <span className="ls-info-icon">⭐</span>
                  <span>Level up → Unlock yield bonuses</span>
                </div>
                <div className="ls-info-item">
                  <span className="ls-info-icon">📦</span>
                  <span>Queue actions for AFK training</span>
                </div>
                <div className="ls-info-item highlight">
                  <span className="ls-info-icon">💡</span>
                  <span>Higher actions = more XP/hr</span>
                </div>
              </div>
            </div>
          </div>

          {/* All Skills Overview */}
          <div className="ls-section ls-skills-overview">
            <div className="ls-section-header">
              <span className="ls-section-title">Skills Overview</span>
            </div>
            <div className="ls-section-body">
              <div className="ls-skills-list">
                {allSkillsInfo.map(skill => (
                  <div key={skill.id} className="ls-skill-row">
                    <div className="ls-skill-row-header">
                      <span className="ls-skill-icon">{skill.icon}</span>
                      <span className="ls-skill-name">{skill.name}</span>
                      <span className="ls-skill-level">Lv.{skill.level}</span>
                    </div>
                    <div className="ls-skill-row-bar">
                      <div
                        className="ls-skill-row-fill"
                        style={{ width: `${skill.progressPercent}%` }}
                      />
                    </div>
                    <div className="ls-skill-row-info">
                      <span className="ls-skill-row-xp">
                        {skill.xp}/{skill.xpForNext} XP
                      </span>
                      {skill.totalBonuses?.yieldBonus > 0 && (
                        <span className="ls-skill-row-bonus">
                          {formatPercent(skill.totalBonuses.yieldBonus)} yield
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Active Bonuses Summary */}
          <div className="ls-section ls-bonuses-section">
            <div className="ls-section-header">
              <span className="ls-section-title">Active Yield Bonuses</span>
            </div>
            <div className="ls-section-body">
              {allSkillsInfo.some(s => s.totalBonuses?.yieldBonus > 0) ? (
                <div className="ls-bonuses-list">
                  {allSkillsInfo
                    .filter(s => s.totalBonuses?.yieldBonus > 0)
                    .map(skill => (
                      <div key={skill.id} className="ls-bonus-row">
                        <span className="ls-bonus-icon">{skill.icon}</span>
                        <span className="ls-bonus-name">{skill.name}</span>
                        <span className="ls-bonus-value">
                          {formatPercent(skill.totalBonuses.yieldBonus)}
                        </span>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="ls-no-bonuses">
                  Reach Lv.5 to unlock yield bonuses
                </div>
              )}
            </div>
          </div>

          {/* Next Milestones */}
          <div className="ls-section ls-milestones-section">
            <div className="ls-section-header">
              <span className="ls-section-title">Next Milestones</span>
            </div>
            <div className="ls-section-body">
              <div className="ls-milestones-list">
                {allSkillsInfo
                  .filter(s => s.nextMilestone)
                  .slice(0, 3)
                  .map(skill => (
                    <div key={skill.id} className="ls-milestone-row">
                      <div className="ls-milestone-header">
                        <span className="ls-milestone-icon">{skill.icon}</span>
                        <span className="ls-milestone-skill">{skill.name}</span>
                        <span className="ls-milestone-level">Lv.{skill.nextMilestone.level}</span>
                      </div>
                      <div className="ls-milestone-desc">
                        {skill.nextMilestone.description}
                      </div>
                    </div>
                  ))}
                {allSkillsInfo.every(s => !s.nextMilestone) && (
                  <div className="ls-no-milestones">
                    All milestones unlocked!
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LifeSkillsPanel;
